// arrow_callout — rótulo em pílula + seta curva desenhada a lápis até um alvo (x,y do
// quadro) + círculo a lápis em volta do alvo. Posiciona-se sozinho (ignora a zona).
// Se o rótulo sairia do quadro na direção pedida, a seta vira para o lado oposto.
import React from 'react';
import { AbsoluteFill, interpolate } from 'remotion';
import type { ArrowCalloutProps, ArrowDirection } from './schema';
import { EASE_BACK, EASE_OUT, FONT, PencilPath, Pop, fitFont, onAccent, prog, textWidth, useGFrame, type GraphicComponentProps } from './common';

const W = 1080;
const H = 1920;
const LEN = 250;
const DIRS: Record<ArrowDirection, [number, number]> = { down: [0, 1], up: [0, -1], left: [-1, 0], right: [1, 0] };
const OPP: Record<ArrowDirection, ArrowDirection> = { down: 'up', up: 'down', left: 'right', right: 'left' };

function layout(tx: number, ty: number, dir: ArrowDirection, lw: number, lh: number) {
  const [dx, dy] = DIRS[dir];
  const extent = dx !== 0 ? lw / 2 : lh / 2;
  const cx = tx - dx * (LEN + 40 + extent) + (dy !== 0 ? -dy * 70 : 0);
  const cy = ty - dy * (LEN + 40 + extent) + (dx !== 0 ? dx * 50 : 0);
  const inside = cx - lw / 2 >= 50 && cx + lw / 2 <= W - 50 && cy - lh / 2 >= 170 && cy + lh / 2 <= H - 200;
  return { cx, cy, inside };
}

export const ArrowCallout: React.FC<GraphicComponentProps<ArrowCalloutProps>> = ({ props, accent }) => {
  const f = useGFrame();
  const tx = props.x * W;
  const ty = props.y * H;
  const size = fitFont(props.label, { weight: 900, max: 52, min: 36, maxWidth: 560 });
  const lw = textWidth(props.label, size, 900) + 64;
  const lh = Math.round(size * 1.2) + 30;

  let dir: ArrowDirection = props.direction;
  let L = layout(tx, ty, dir, lw, lh);
  if (!L.inside) {
    const alt = layout(tx, ty, OPP[dir], lw, lh);
    if (alt.inside) { dir = OPP[dir]; L = alt; }
  }
  const cx = Math.max(lw / 2 + 50, Math.min(W - 50 - lw / 2, L.cx));
  const cy = Math.max(170 + lh / 2, Math.min(H - 200 - lh / 2, L.cy));
  const [dx, dy] = DIRS[dir];
  const extent = dx !== 0 ? lw / 2 : lh / 2;

  // seta: sai da borda do rótulo e para um pouco antes do alvo, curvando para o lado
  const sx = cx + dx * (extent + 22);
  const sy = cy + dy * (extent + 22);
  const ex = tx - dx * 46;
  const ey = ty - dy * 46;
  const mx = (sx + ex) / 2;
  const my = (sy + ey) / 2;
  const len = Math.hypot(ex - sx, ey - sy) || 1;
  const px = -(ey - sy) / len;
  const py = (ex - sx) / len;
  const bend = Math.min(90, len * 0.28);
  const qx = mx + px * bend;
  const qy = my + py * bend;
  const d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} Q ${qx.toFixed(1)} ${qy.toFixed(1)} ${ex.toFixed(1)} ${ey.toFixed(1)}`;
  // ponta: tangente no fim (fim − controle)
  const tl = Math.hypot(ex - qx, ey - qy) || 1;
  const ux = (ex - qx) / tl;
  const uy = (ey - qy) / tl;
  const head = (ang: number) => {
    const c = Math.cos(ang);
    const s = Math.sin(ang);
    const bx = -ux * c + uy * s;
    const by = -ux * s - uy * c;
    return `${(ex + bx * 44).toFixed(1)} ${(ey + by * 44).toFixed(1)}`;
  };
  const headD = `M ${head(0.55)} L ${ex.toFixed(1)} ${ey.toFixed(1)} L ${head(-0.55)}`;
  const ring =
    `M ${tx - 66} ${ty + 6} C ${tx - 70} ${ty - 44}, ${tx + 48} ${ty - 58}, ${tx + 70} ${ty - 10} ` +
    `C ${tx + 88} ${ty + 34}, ${tx - 20} ${ty + 62}, ${tx - 58} ${ty + 32} C ${tx - 74} ${ty + 18}, ${tx - 62} ${ty - 14}, ${tx - 40} ${ty - 26}`;

  const labelIn = prog(f, 0, 8, EASE_BACK);
  const draw = prog(f, 5, 15, EASE_OUT);
  const headP = prog(f, 14, 19, EASE_OUT);
  const ringP = prog(f, 16, 27, EASE_OUT);
  const wobble = Math.sin(f * 0.18) * 1.2;

  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      <Pop at={15} />
      <svg viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
        <PencilPath d={d} progress={draw} color="#fff" width={10} />
        <PencilPath d={headD} progress={headP} color="#fff" width={10} />
        <PencilPath d={ring} progress={ringP} color={accent} width={8} />
      </svg>
      <div
        style={{
          position: 'absolute',
          left: cx - lw / 2,
          top: cy - lh / 2,
          width: lw,
          height: lh,
          borderRadius: 20,
          background: accent,
          color: onAccent(accent),
          fontFamily: FONT,
          fontWeight: 900,
          fontSize: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          whiteSpace: 'nowrap',
          letterSpacing: -0.5,
          boxShadow: '0 14px 36px rgba(0,0,0,0.45)',
          opacity: Math.min(1, labelIn * 1.5),
          scale: String(interpolate(labelIn, [0, 1], [0.6, 1])),
          rotate: `${(-3 + wobble).toFixed(2)}deg`,
        }}
      >
        {props.label}
      </div>
    </AbsoluteFill>
  );
};
